
import { dig } from "@matchlighter/common_library/lib/macro";
import { env } from "@/environment";
import { FieldState, getIn } from "final-form";

const EMAIL_REGEXP = dig(env.regexps.email_address) || /^([a-zA-Z0-9_]+[\-\+\.])*[a-zA-Z0-9_]+@([a-zA-Z0-9_\-]+\.)+([a-zA-Z]{2,5})$/

// Validators
export const isNotBlank = (value: string) => !(value && value.toString().trim()) ? 'Cannot be empty' : undefined;
export const isTruthy = (value: any) => !value ? 'Cannot be empty' : undefined;
export const isPresent = (value: any) => (!value || value.length == 0) ? 'Cannot be empty' : undefined;
export const maxLength = (length: number) => (value: string) => (value && value.length > length) ? `May not be longer than ${length} characters` : undefined;
export const minLength = (length: number) => (value: string) => (value && value.length < length) ? `May not be shorter than ${length} characters` : undefined;
export const matches = (pattern: RegExp, humanPattern?: string) => (value: string) => {
    if (pattern.test(value || '')) return undefined;
    if (humanPattern) return humanPattern;
    return `Must match ${String(pattern)}`
}
export const isAlphanumeric = matches(/^\w*$/, 'Must be alphanumeric');
export const looksLikeAnEmail = matches(EMAIL_REGEXP, 'Must be an email address')
export const isNumeric = matches(/^\d+$/, 'Must be Numeric')

export const composeValidators = (...validators) => (...args) =>
    validators.reduce((error, validator) => error || validator(...args), undefined);

export const validDate = (value) => value === 'INVALID' ? true : undefined;
export const requiredDate = (value) => (!value || value === 'INVALID') ? true : undefined;

export const range_validator = (message: string, start_key = 'start_date', end_key = 'end_date') => {
    return (value, allValues, meta: FieldState<any>) => {
        const pfx = meta.name.split('.').slice(0, -1).join('.');
        const values = getIn(allValues, pfx);

        if (!values) return;
        if (!values[start_key] || !values[end_key]) return;
        if (values[start_key] > values[end_key]) {
            return message;
        }
    }
}

// Parse / Format
export const PARSE_INT = (v) => {
    if (typeof v == 'string') v = parseInt(v);
    if (isNaN(v)) return null;
    return v;
}
export const PARSE_NUMBER = (v) => {
    if (typeof v == 'string') v = parseFloat(v);
    if (isNaN(v)) return null;
    return v;
}

export const PARSE_GRADE = (v) => {
    // using includes() b/c of substrings
    // also you can do v >= 0 even though it's a string because JavaScript...
    if ((!isNaN(v) && v >= 0) || 'EX'.includes(v) || 'MI'.includes(v)) {
        return v;
    } else {
        return null;
    }
}

export const PARSE_ID = (v) => typeof v == 'string' ? parseInt(v) : v;
export const FORMAT_ID = (v) => v ? v.toString() : v;

export const IDENTITY = (v) => v;

// Mixins
export const NO_FORMAT = { parse: IDENTITY, format: IDENTITY, allowNull: true };
export const ID_FIELD = { parse: PARSE_ID, format: FORMAT_ID };

// Misc
export function mapValues<T>(obj: T, map: { [k: string]: keyof T }) {
    const out = {}
    for (let [nk, ok] of Object.entries(map)) {
        out[nk] = obj[ok];
    }
    return out;
}


interface RootContext {
    formData?: { [k: string]: string | Blob };
}
interface Context {
    rootContext: RootContext;
    path: (string | number)[];
}

function urlJoinPath(path: (string | number)[]) {
    let result: string;
    for (let p of path) {
        if (!result) {
            result = p.toString();
        } else {
            result += `[${p}]`
        }
    }
    return result;
}

function internalSerializeFormData(value, context: Context) {
    const rootContext = context.rootContext;
    if (Array.isArray(value)) {
        return value.map((v, i) => internalSerializeFormData(v, {
            ...context,
            path: [...context.path, i],
        }));
    } else if (value instanceof File || value instanceof Blob) {
        if (!rootContext.formData) rootContext.formData = {}
        rootContext.formData[urlJoinPath(context.path)] = value;
        return undefined;
    } else if (value && typeof value == 'object') {
        const nvalue: any = {}
        for (let [k, v] of Object.entries(value)) {
            nvalue[k] = internalSerializeFormData(v, {
                ...context,
                path: [...context.path, k],
            })
        }
        return nvalue;
    } else {
        return value;
    }
}

export function serializeFormData(data) {
    const root_context: RootContext = {};
    const outValue = internalSerializeFormData(data, { rootContext: root_context, path: [] });
    if (root_context.formData) {
        const formData = new FormData();
        formData.append('_parameters', JSON.stringify(outValue));
        for (let [k, v] of Object.entries(root_context.formData)) {
            formData.append(k, v, (v as any).name);
        }
        return formData;
    } else {
        return outValue;
    }
}

export function asFileUpload(thing) {
    if (thing === null) return null;

    if (thing instanceof Blob) {
        return thing;
    } {
        return undefined;
    }
}
